import type { CompanyShellValidationError } from "../types";

/** `${row_index}:${column}` — a stable key for one cell's errors. */
export function cellErrorKey(rowIndex: number, column: string): string {
  return `${rowIndex}:${column}`;
}

/**
 * Backend sentinel meaning "this error isn't about any specific row" — a
 * whole-invocation failure (e.g. a scope-assertion failure), always routed
 * to {@link MappedValidationErrors.batchErrors} regardless of `row_index`.
 * Confirmed against two live `company_shell_provision` responses: every
 * genuine per-row error carries a real column name (`company_ird_no`,
 * `week_start_day`, ...), and `column: "*"` only ever appeared on a
 * scope-assertion failure — so unlike `row_index`, `column` reliably
 * disambiguates the two cases even when they'd otherwise look identical
 * (e.g. a 1-row batch, where both would have `row_index: 0`). A row-level
 * error that genuinely *is* tied to a row but not one column (e.g.
 * "Company Code already exists") is expected to omit `column` entirely,
 * not send `"*"` — that case is filed under {@link rowErrors}.
 */
const WHOLE_INVOCATION_COLUMN_SENTINEL = "*";

export interface MappedValidationErrors {
  /** Column-level errors, keyed by {@link cellErrorKey}. Multiple errors on the same cell are concatenated, not last-wins. */
  cellErrors: Map<string, string[]>;
  /** Row-level errors with no `column` (tied to a row, not a specific cell), keyed by `row_index`. */
  rowErrors: Map<number, string[]>;
  /**
   * All anchored error messages grouped by 0-based `row_index` (both
   * column-level and row-level), for a per-row "Checks" summary. A row with
   * no entry (or an empty list) passed validation.
   */
  errorsByRow: Map<number, string[]>;
  /**
   * Error messages not anchored to any rendered row — either an
   * out-of-range `row_index` (negative, or at/past `rowCount`), or a
   * {@link WHOLE_INVOCATION_COLUMN_SENTINEL} failure, which is never row-anchored
   * regardless of its `row_index`. These would otherwise be counted but
   * never displayed, or misattributed to an unrelated row.
   */
  batchErrors: string[];
}

/**
 * Group the shared `{ row_index, column, message, severity }` validate
 * errors into cell-level, row-level, and batch-level buckets for the Review
 * grid — a column-level error highlights that cell; a row-level error (no
 * `column`) renders under the row; and an error that isn't about any
 * rendered row — an out-of-range `row_index`, or the
 * {@link WHOLE_INVOCATION_COLUMN_SENTINEL} `"*"` column — is surfaced
 * separately so it is never silently counted-but-hidden, or misattributed
 * to a row it isn't actually about. `row_index` is 0-based — the same
 * positional index as the submitted `rows` array (confirmed against a live
 * `company_shell_provision` agent response; row 1 of a batch comes back as
 * `row_index: 0`). Pure and unit-testable in isolation from the invocation
 * hooks that produce the raw error list.
 *
 * @param rowCount number of rendered data rows. When provided, an error
 *   whose `row_index` is outside `[0, rowCount)` is treated as batch-level.
 */
export function mapValidationErrorsToCells(
  errors: CompanyShellValidationError[],
  rowCount?: number
): MappedValidationErrors {
  const cellErrors = new Map<string, string[]>();
  const rowErrors = new Map<number, string[]>();
  const errorsByRow = new Map<number, string[]>();
  const batchErrors: string[] = [];

  const append = (
    map: Map<string, string[]>,
    key: string,
    message: string
  ) => {
    const existing = map.get(key);
    if (existing) existing.push(message);
    else map.set(key, [message]);
  };

  const appendRow = (map: Map<number, string[]>, key: number, message: string) => {
    const existing = map.get(key);
    if (existing) existing.push(message);
    else map.set(key, [message]);
  };

  for (const error of errors) {
    const isWholeInvocation = error.column === WHOLE_INVOCATION_COLUMN_SENTINEL;
    const anchoredToRow =
      !isWholeInvocation &&
      error.row_index >= 0 &&
      (rowCount === undefined || error.row_index < rowCount);

    if (!anchoredToRow) {
      batchErrors.push(error.message);
      continue;
    }

    // Every anchored error also feeds the per-row Checks summary.
    appendRow(errorsByRow, error.row_index, error.message);

    if (error.column) {
      append(cellErrors, cellErrorKey(error.row_index, error.column), error.message);
    } else {
      appendRow(rowErrors, error.row_index, error.message);
    }
  }

  return { cellErrors, rowErrors, errorsByRow, batchErrors };
}
